import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, BarChart3, Info } from "lucide-react";
import { api } from "../../api";
import { Badge } from "../../components/ui/badge";
import { RangePicker, KPI, Delta, TrendChart, Breakdown, BarsChart, PrivacyNote } from "./StatsWidgets";
import { downloadCsv } from "./QRStats";

export default function CompanyStats() {
  const [range, setRange] = useState("30d");
  const [custom, setCustom] = useState(null);
  const [data, setData] = useState(null);
  const params = { range, ...(range === "custom" && custom ? custom : {}) };

  useEffect(() => { api.analyticsOverview(params).then(({ data }) => setData(data)); }, [range, custom]); // eslint-disable-line
  if (!data) return <div className="text-muted-foreground">Caricamento…</div>;
  const { totals, qrs, range: r } = data;
  const dyn = qrs.filter((q) => q.dynamic);
  const max = Math.max(1, ...dyn.map((q) => q.scans_period));

  return (
    <div data-testid="company-stats-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-extrabold">Statistiche</h1>
          <p className="text-muted-foreground mt-1">Quante persone usano i tuoi QR, quando e da quale dispositivo.</p>
        </div>
        <button onClick={() => downloadCsv(api.analyticsCsvUrl(params), `qora-statistiche-${range}.csv`)} className="text-sm font-semibold text-primary hover:underline" data-testid="export-csv">Esporta CSV</button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <KPI label="Scansioni totali" value={totals.all_time} testid="kpi-total" />
        <KPI label="Oggi" value={totals.today} testid="kpi-today" />
        <KPI label="Ultimi 7 giorni" value={totals.last7} testid="kpi-7d" />
        <KPI label="Ultimi 30 giorni" value={totals.last30} testid="kpi-30d" />
      </div>

      <RangePicker value={range} custom={custom} onChange={(k, c) => { setRange(k); if (c) setCustom(c); }} />
      <div className="flex flex-wrap items-center gap-3 text-sm my-4">
        <span className="text-muted-foreground">Periodo {r.from} → {r.to}:</span>
        <b data-testid="period-total">{data.period} scansioni</b>
        <Delta pct={data.change_pct} />
        <span className="text-muted-foreground">· {data.estimated_visitors} dispositivi stimati</span>
      </div>

      <div className="grid lg:grid-cols-3 gap-3 mb-3">
        <div className="lg:col-span-2"><TrendChart series={data.series} gran={r.granularity} height={260} /></div>
        <Breakdown title="Dispositivo" items={data.by_device} testid="breakdown-device" />
      </div>

      {/* QR comparison */}
      <div className="rounded-2xl border border-border bg-white p-4 card-shadow mb-3" data-testid="qr-comparison">
        <div className="flex items-center justify-between mb-3">
          <p className="font-semibold">QR più utilizzati nel periodo</p>
          <span className="text-xs text-muted-foreground">{data.dynamic_count} dinamici · {data.static_count} statici</span>
        </div>
        {dyn.length === 0 ? <p className="text-sm text-muted-foreground">Nessun QR dinamico. Crea un QR dinamico per iniziare a misurare le scansioni.</p> : (
          <div className="space-y-3">
            {dyn.map((q, i) => (
              <Link key={q.id} to={`/app/qr/${q.id}/statistiche`} className="block group" data-testid={`compare-row-${q.id}`}>
                <div className="flex items-center justify-between gap-3 text-sm">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="h-6 w-6 rounded-full bg-muted grid place-items-center text-[11px] font-bold shrink-0">{i + 1}</span>
                    <span className="font-medium truncate group-hover:text-primary">{q.name}</span>
                    {q.status === "paused" && <Badge variant="outline" className="text-[10px] text-amber-600">pausa</Badge>}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="tabular-nums font-semibold">{q.scans_period}</span>
                    <span className={`text-xs tabular-nums w-16 text-right ${q.change_pct === null ? "text-muted-foreground" : q.change_pct > 0 ? "text-emerald-600" : q.change_pct < 0 ? "text-rose-600" : "text-muted-foreground"}`}>{q.change_pct === null ? "—" : `${q.change_pct > 0 ? "+" : ""}${q.change_pct}%`}</span>
                    <span className="text-xs text-muted-foreground w-20 text-right hidden sm:inline">{q.scans_all_time} totali</span>
                  </div>
                </div>
                <div className="h-2 rounded-full bg-muted mt-1.5 overflow-hidden ml-8"><div className="h-full rounded-full bg-primary/85 transition-[width]" style={{ width: `${Math.round(q.scans_period / max * 100)}%` }} /></div>
              </Link>
            ))}
          </div>
        )}
        {data.static_count > 0 && (
          <p className="mt-4 text-xs text-muted-foreground flex items-start gap-1.5" data-testid="static-notice"><Info className="h-3.5 w-3.5 mt-0.5 shrink-0" /> I {data.static_count} QR statici non compaiono: aprono direttamente la destinazione e le loro scansioni non possono essere rilevate.</p>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-3 mb-3">
        <BarsChart title="Giorno della settimana" items={data.by_weekday} testid="chart-weekday" />
        <BarsChart title="Fascia oraria" items={data.by_hour} testid="chart-hour" />
      </div>
      <div className="grid md:grid-cols-2 gap-3 mb-4">
        <Breakdown title="Sistema operativo" items={data.by_os} testid="breakdown-os" />
        <Breakdown title="Browser" items={data.by_browser} testid="breakdown-browser" />
      </div>
      <PrivacyNote />
    </div>
  );
}
